import { useLocalStorageState } from "ahooks";
import React, {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

/**
 * Edge and corner handle types for resizing.
 * - left/right: width only
 * - top/bottom: height only
 * - corners: width + height
 */
export type ResizeHandle =
  | "left"
  | "right"
  | "top"
  | "bottom"
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right";

const CURSOR_MAP: Record<ResizeHandle, string> = {
  left: "col-resize",
  right: "col-resize",
  top: "row-resize",
  bottom: "row-resize",
  "top-left": "nwse-resize",
  "top-right": "nesw-resize",
  "bottom-left": "nesw-resize",
  "bottom-right": "nwse-resize",
};

/**
 * Calculate responsive width based on viewport size
 * When viewport > 1024px, returns 40% of viewport width (constrained 600px-800px)
 * Otherwise returns 600px minimum
 */
function calculateResponsiveWidth(viewportWidth: number): number {
  if (viewportWidth > 1024) {
    const responsiveWidth = viewportWidth * 0.4;
    return Math.max(600, Math.min(800, responsiveWidth));
  }
  return 600;
}

export interface UseResizableOptions {
  /**
   * The direction of the resize handle (legacy; use directions for multiple)
   * @default "right"
   */
  direction?: "left" | "right";
  /**
   * Initial width of the resizable element
   * @default calculated responsive width (40% of viewport when > 1024px, constrained 600px-800px)
   */
  initialWidth?: number;
  /**
   * Minimum width constraint
   * @default 600
   */
  minWidth?: number;
  /**
   * Maximum width constraint
   * @default 800
   */
  maxWidth?: number;
  /**
   * Initial height (used when directions include top, bottom, or corners)
   */
  initialHeight?: number;
  /**
   * Minimum height constraint
   */
  minHeight?: number;
  /**
   * Maximum height constraint
   */
  maxHeight?: number;
  /**
   * Storage key for localStorage persistence
   * @default "resizable-width"
   */
  storageKey?: string;
  /**
   * Callback fired when width changes
   */
  onWidthChange?: (width: number) => void;
  /**
   * Callback fired while the user drags a width handle (live preview, not persisted until drag ends).
   */
  onWidthChangeEvent?: (width: number) => void;
  /**
   * Callback fired when the user begins dragging a resize handle.
   */
  onResizeStart?: () => void;
  /**
   * Callback fired when the user finishes dragging a resize handle.
   */
  onResizeEnd?: () => void;
  /**
   * Callback fired when height changes
   */
  onHeightChange?: (height: number) => void;
  /**
   * Whether the resizable functionality is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Override width applied to the DOM without changing stored width (e.g. isClosed collapse).
   */
  renderedWidth?: number;
  /**
   * Override height applied to the DOM without changing stored height (e.g. isClosed collapse).
   */
  renderedHeight?: number;
}

export interface UseResizableReturn {
  /** Current width of the element */
  width: number;
  /** Current height (only when height options are provided) */
  height?: number;
  /** Whether the user is currently dragging */
  isDragging: boolean;
  /** Ref to attach to the resizable element */
  elementRef: RefObject<HTMLDivElement | null>;
  /** Mouse down handler; pass the handle type for the handle being dragged (default "right" for legacy single-handle usage) */
  handleMouseDown: (e: React.MouseEvent, handle?: ResizeHandle) => void;
  /** Function to programmatically set width */
  setWidth: (width: number) => void;
  /** Function to programmatically set height (when height is enabled) */
  setHeight?: (height: number) => void;
  /** Function to reset width to initial value */
  resetWidth: () => void;
  /** Function to reset height to initial value (when height is enabled) */
  resetHeight?: () => void;
}

function isHorizontalHandle(handle: ResizeHandle): boolean {
  return handle === "left" || handle === "right";
}

function isVerticalHandle(handle: ResizeHandle): boolean {
  return handle === "top" || handle === "bottom";
}

function isCornerHandle(handle: ResizeHandle): boolean {
  return ["top-left", "top-right", "bottom-left", "bottom-right"].includes(
    handle,
  );
}

function getWidthSign(handle: ResizeHandle): number {
  switch (handle) {
    case "left":
    case "top-left":
    case "bottom-left":
      return -1;
    case "right":
    case "top-right":
    case "bottom-right":
      return 1;
    default:
      return 0;
  }
}

function getHeightSign(handle: ResizeHandle): number {
  switch (handle) {
    case "top":
    case "top-left":
    case "top-right":
      return -1;
    case "bottom":
    case "bottom-left":
    case "bottom-right":
      return 1;
    default:
      return 0;
  }
}

/**
 * Custom hook for resizable panel functionality
 * Provides drag-to-resize behavior with constraints and persistence
 */
export function useResizable({
  initialWidth,
  minWidth = 600,
  maxWidth = 800,
  initialHeight,
  minHeight = 200,
  maxHeight,
  storageKey = "resizable-width",
  onWidthChange,
  onWidthChangeEvent,
  onResizeStart,
  onResizeEnd,
  onHeightChange,
  disabled = false,
  renderedWidth,
  renderedHeight,
}: UseResizableOptions = {}): UseResizableReturn {
  const elementRef = useRef<HTMLDivElement>(null);
  const hasHeight = initialHeight !== undefined;
  const onWidthChangeEventRef = useRef(onWidthChangeEvent);
  onWidthChangeEventRef.current = onWidthChangeEvent;
  const onResizeStartRef = useRef(onResizeStart);
  onResizeStartRef.current = onResizeStart;
  const onResizeEndRef = useRef(onResizeEnd);
  onResizeEndRef.current = onResizeEnd;
  const lastWidthChangeEventRef = useRef<number | null>(null);

  // Calculate initial responsive width based on viewport
  const getInitialWidth = useCallback(() => {
    const responsiveWidth = calculateResponsiveWidth(window.innerWidth);
    const targetWidth = initialWidth ?? responsiveWidth;
    return Math.max(minWidth, Math.min(maxWidth, targetWidth));
  }, [initialWidth, minWidth, maxWidth]);

  const effectiveMaxHeight = maxHeight ?? Number.POSITIVE_INFINITY;

  const getInitialHeight = useCallback(() => {
    const target = initialHeight ?? 400;
    return Math.max(minHeight, Math.min(effectiveMaxHeight, target));
  }, [initialHeight, minHeight, effectiveMaxHeight]);

  const [width, setWidth] = useLocalStorageState(storageKey, {
    defaultValue: getInitialWidth(),
  });

  const [height, setHeightState] = useLocalStorageState(
    `${storageKey}-height`,
    {
      defaultValue: getInitialHeight(),
    },
  );

  const [isDragging, setIsDragging] = useState(false);
  const dragStartXRef = useRef(0);
  const dragStartYRef = useRef(0);
  const initialDragWidthRef = useRef(0);
  const initialDragHeightRef = useRef(0);
  const currentWidthRef = useRef(width ?? getInitialWidth());
  const currentHeightRef = useRef(
    hasHeight ? (height ?? getInitialHeight()) : 0,
  );
  const activeHandleRef = useRef<ResizeHandle>("right");
  const rafIdRef = useRef<number | null>(null);
  const maxWidthAtDragStartRef = useRef(maxWidth);
  const maxHeightAtDragStartRef = useRef(effectiveMaxHeight);

  const updateWidth = useCallback(
    (newWidth: number) => {
      const constrainedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
      setWidth(constrainedWidth);
      onWidthChange?.(constrainedWidth);
    },
    [minWidth, maxWidth, setWidth, onWidthChange],
  );

  // Notify consumers of the initial width (from storage / defaults) — same as after resize.
  useLayoutEffect(() => {
    if (disabled || !onWidthChange) return;
    const w = width ?? getInitialWidth();
    const constrained = Math.max(minWidth, Math.min(maxWidth, w));
    onWidthChange(constrained);
    // Intentionally once per hook mount; width updates from drag/programmatic paths call onWidthChange via updateWidth.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateHeight = useCallback(
    (newHeight: number) => {
      const constrainedHeight = Math.max(
        minHeight,
        Math.min(effectiveMaxHeight, newHeight),
      );
      setHeightState(constrainedHeight);
      onHeightChange?.(constrainedHeight);
    },
    [minHeight, effectiveMaxHeight, setHeightState, onHeightChange],
  );

  useLayoutEffect(() => {
    if (disabled || !hasHeight || !onHeightChange) return;
    const h = height ?? getInitialHeight();
    const constrained = Math.max(minHeight, Math.min(effectiveMaxHeight, h));
    onHeightChange(constrained);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, handle: ResizeHandle = "right") => {
      if (disabled || !elementRef.current) return;

      const el = elementRef.current;
      const hasW = isHorizontalHandle(handle) || isCornerHandle(handle);
      const hasH =
        hasHeight && (isVerticalHandle(handle) || isCornerHandle(handle));

      if (!hasW && !hasH) return;

      setIsDragging(true);
      dragStartXRef.current = e.clientX;
      dragStartYRef.current = e.clientY;
      initialDragWidthRef.current = el.offsetWidth;
      initialDragHeightRef.current = el.offsetHeight;
      currentWidthRef.current = el.offsetWidth;
      currentHeightRef.current = el.offsetHeight;
      lastWidthChangeEventRef.current = null;
      activeHandleRef.current = handle;
      maxWidthAtDragStartRef.current = maxWidth;
      maxHeightAtDragStartRef.current = effectiveMaxHeight;
      onResizeStartRef.current?.();
      e.preventDefault();
    },
    [disabled, hasHeight, maxWidth, effectiveMaxHeight],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || disabled || !elementRef.current) return;

      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }

      const handle = activeHandleRef.current;
      const widthSign = getWidthSign(handle);
      const heightSign = getHeightSign(handle);

      rafIdRef.current = requestAnimationFrame(() => {
        if (!elementRef.current) return;

        const el = elementRef.current;
        const deltaX = e.clientX - dragStartXRef.current;
        const deltaY = e.clientY - dragStartYRef.current;

        if (widthSign !== 0) {
          const newWidth = initialDragWidthRef.current + deltaX * widthSign;
          const constrainedWidth = Math.max(
            minWidth,
            Math.min(maxWidthAtDragStartRef.current, newWidth),
          );
          el.style.width = `${constrainedWidth}px`;
          currentWidthRef.current = constrainedWidth;
          if (lastWidthChangeEventRef.current !== constrainedWidth) {
            lastWidthChangeEventRef.current = constrainedWidth;
            onWidthChangeEventRef.current?.(constrainedWidth);
          }
        }

        if (hasHeight && heightSign !== 0) {
          const newHeight = initialDragHeightRef.current + deltaY * heightSign;
          const constrainedHeight = Math.max(
            minHeight,
            Math.min(maxHeightAtDragStartRef.current, newHeight),
          );
          el.style.height = `${constrainedHeight}px`;
          currentHeightRef.current = constrainedHeight;
        }
      });
    },
    [isDragging, disabled, hasHeight, minWidth, minHeight],
  );

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;

    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    setIsDragging(false);

    const handle = activeHandleRef.current;
    if (getWidthSign(handle) !== 0) {
      updateWidth(currentWidthRef.current);
    }
    if (hasHeight && getHeightSign(handle) !== 0) {
      updateHeight(currentHeightRef.current);
    }
    onResizeEndRef.current?.();
  }, [isDragging, hasHeight, updateWidth, updateHeight]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor =
        CURSOR_MAP[activeHandleRef.current] ?? "col-resize";
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";

        if (rafIdRef.current !== null) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (elementRef.current && !isDragging) {
      const w = renderedWidth ?? width;
      if (w != null) {
        elementRef.current.style.width = `${w}px`;
        currentWidthRef.current = w;
      }
    }
  }, [width, renderedWidth, isDragging]);

  useEffect(() => {
    if (
      hasHeight &&
      elementRef.current &&
      !isDragging &&
      height !== undefined
    ) {
      const h = renderedHeight ?? height;
      elementRef.current.style.height = `${h}px`;
      currentHeightRef.current = h;
    }
  }, [hasHeight, height, renderedHeight, isDragging]);

  useEffect(() => {
    if (width && (width > maxWidth || width < minWidth)) {
      updateWidth(width);
    }
  }, [width, maxWidth, minWidth, updateWidth]);

  useEffect(() => {
    if (
      hasHeight &&
      height !== undefined &&
      (height > effectiveMaxHeight || height < minHeight)
    ) {
      updateHeight(height);
    }
  }, [hasHeight, height, effectiveMaxHeight, minHeight, updateHeight]);

  const setWidthProgrammatically = useCallback(
    (newWidth: number) => {
      updateWidth(newWidth);
    },
    [updateWidth],
  );

  const setHeightProgrammatically = useCallback(
    (newHeight: number) => {
      updateHeight(newHeight);
    },
    [updateHeight],
  );

  const resetWidth = useCallback(() => {
    updateWidth(initialWidth ?? calculateResponsiveWidth(window.innerWidth));
  }, [updateWidth, initialWidth]);

  const resetHeight = useCallback(() => {
    updateHeight(initialHeight ?? 400);
  }, [updateHeight, initialHeight]);

  // On window resize, only clamp the stored width into bounds (never wider
  // than the window). Snapping to a "responsive default" here would stomp
  // widths the user chose by dragging.
  useEffect(() => {
    if (disabled) return;

    const handleResize = () => {
      if (isDragging) return;

      const currentWidthVal = width ?? getInitialWidth();
      const upperBound = Math.min(maxWidth, window.innerWidth);

      if (currentWidthVal > upperBound || currentWidthVal < minWidth) {
        updateWidth(Math.min(currentWidthVal, upperBound));
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [
    disabled,
    isDragging,
    width,
    minWidth,
    maxWidth,
    getInitialWidth,
    updateWidth,
  ]);

  const result: UseResizableReturn = {
    width: width ?? getInitialWidth(),
    isDragging,
    elementRef,
    handleMouseDown,
    setWidth: setWidthProgrammatically,
    resetWidth,
  };

  if (hasHeight) {
    result.height = height ?? getInitialHeight();
    result.setHeight = setHeightProgrammatically;
    result.resetHeight = resetHeight;
  }

  return result;
}
